import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UiMenubarItem } from './menubar.types';

export type { UiMenubarItem };

/**
 * Organismo: barra de menú horizontal (navbar / menubar).
 * Casos de uso alineados con [PrimeReact Menubar](https://primereact.org/menubar/):
 * - **Basic**: `items` como modelo.
 * - **Template**: slots `data-ui-menubar-start` / `data-ui-menubar-end`.
 * - **Command**: salida `itemSelect` al activar una hoja (equivalente a `command`).
 * - **Router / URL**: `routerLink` y `href` en ítems hoja.
 * - **Submenú**: ítem raíz con `items[]` abre panel; un hijo puede anidar otro `items[]` (flyout).
 * - **Iconos**: cada ítem puede incluir o no `iconSrc` en la barra y en las hojas del submenú.
 */
@Component({
  selector: 'ui-menubar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  templateUrl: './menubar.component.html',
  styleUrl: './menubar.component.scss',
  host: {
    class: 'ui-menubar-host',
  },
})
export class UiMenubarComponent {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);

  readonly items = input<readonly UiMenubarItem[]>([]);

  /** Etiqueta accesible del `nav` */
  readonly ariaLabel = input('Menú principal');

  /** Una sola fila con scroll horizontal cuando los ítems no caben. */
  readonly horizontalScroll = input(false);

  /** Equivalente a `command` en PrimeReact: activación de un ítem hoja */
  readonly itemSelect = output<{ id: string; item: UiMenubarItem }>();

  /** Id del submenú raíz abierto (solo uno a la vez) */
  protected readonly openSubmenuId = signal<string | null>(null);

  /** Id del submenú anidado abierto dentro del panel raíz */
  protected readonly openNestedSubmenuId = signal<string | null>(null);

  /** Posición fija del submenú cuando el scroll horizontal recortaría el panel. */
  protected readonly submenuPosition = signal<{ top: number; left: number } | null>(
    null,
  );

  protected toggleSubmenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openSubmenuId.update(cur => {
      if (cur === id) {
        this.openNestedSubmenuId.set(null);
        this.submenuPosition.set(null);
        return null;
      }
      this.openNestedSubmenuId.set(null);
      this.placeSubmenu(event.currentTarget);
      return id;
    });
  }

  protected toggleNestedSubmenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openNestedSubmenuId.update(cur => (cur === id ? null : id));
  }

  protected closeSubmenus(): void {
    this.openSubmenuId.set(null);
    this.openNestedSubmenuId.set(null);
    this.submenuPosition.set(null);
  }

  protected onListScroll(event: Event): void {
    if (!this.horizontalScroll() || !this.openSubmenuId()) return;
    const left = (event.currentTarget as HTMLElement).scrollLeft;
    if (left === this.listScrollLeft) return;
    this.listScrollLeft = left;
    this.closeSubmenus();
  }

  private listScrollLeft = 0;

  private placeSubmenu(target: EventTarget | null): void {
    if (!this.horizontalScroll() || !(target instanceof HTMLElement)) {
      this.submenuPosition.set(null);
      return;
    }
    this.listScrollLeft = target.closest('.ui-menubar__list')?.scrollLeft ?? 0;
    const rect = target.getBoundingClientRect();
    this.submenuPosition.set({ top: rect.bottom + 4, left: rect.left });
  }

  protected activateLeaf(item: UiMenubarItem, event?: Event): void {
    if (item.disabled || item.separator) {
      event?.preventDefault();
      return;
    }
    // Navegar antes de cerrar el submenú: al destruir el <a> el routerLink
    // del click puede cancelarse y la hoja “no hace nada”.
    if (item.routerLink) {
      event?.preventDefault();
      void this.router.navigateByUrl(item.routerLink);
    }
    this.itemSelect.emit({ id: item.id, item });
    this.closeSubmenus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.openSubmenuId()) return;
    const t = ev.target as Node;
    if (this.hostEl.nativeElement.contains(t)) return;
    this.closeSubmenus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.openSubmenuId()) {
      this.closeSubmenus();
    }
  }

  protected submenuPanelId(rootId: string): string {
    return `ui-menubar-sub-${rootId}`;
  }
}
